import { NotificationVariants, request, type ExecTypeEnum } from '@talos/kyoko';
import type { ManualFillCareOrderEntry } from './manualFillCareOrdersSlice';

interface ManualFillCareOrderPayload {
  ExecType: ExecTypeEnum;
  FillQty: string;
  FillPrice?: string;
  FillAmount?: string;
  LegSummary?: ManualFillCareOrderLegPayload[];
}

interface ManualFillCareOrderLegPayload {
  Symbol: string;
  FillQty: string;
  FillPrice?: string;
  FillAmount?: string;
}

const MANUAL_FILL_EXEC_TYPE: ExecTypeEnum = 'DoneForDay';

export async function manuallyFillCareOrders({
  entries,
  orgApiEndpoint,
  addToast,
}: {
  entries: ManualFillCareOrderEntry[];
  orgApiEndpoint: string;
  addToast: (toast: { text: string; variant: NotificationVariants }) => void;
}) {
  const results = await Promise.allSettled(entries.map(entry => manuallyFillCareOrder({ entry, orgApiEndpoint })));
  const fulfilledCount = results.filter(result => result.status === 'fulfilled').length;
  const rejectedCount = results.length - fulfilledCount;
  const successfulCareOrderIDs = results.flatMap((result, index) =>
    result.status === 'fulfilled' ? [entries[index].careOrder.OrderID] : []
  );

  // Requests are independent, so report per-row failures without hiding successful fills.
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      return;
    }

    const careOrderID = entries.at(index)?.careOrder.OrderID;
    addToast({
      text: `Failed to manually fill care order ${careOrderID ?? ''}: ${getManualFillRequestErrorMessage(result.reason)}`,
      variant: NotificationVariants.Negative,
    });
  });

  if (fulfilledCount > 0) {
    addToast({
      text: `Successfully manually filled ${fulfilledCount} care order(s).`,
      variant: NotificationVariants.Positive,
    });
  }

  if (rejectedCount > 0) {
    addToast({
      text: `Failed to manually fill ${rejectedCount} care order(s).`,
      variant: NotificationVariants.Negative,
    });
  }

  return { allSucceeded: rejectedCount === 0, successfulCareOrderIDs };
}

function manuallyFillCareOrder({ entry, orgApiEndpoint }: { entry: ManualFillCareOrderEntry; orgApiEndpoint: string }) {
  return request(
    'POST',
    `${orgApiEndpoint}/care/orders/${entry.careOrder.OrderID}/manual-fill`,
    buildManualFillCareOrderPayload(entry)
  );
}

function getManualFillRequestErrorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : 'Unknown error';
}

function buildManualFillCareOrderPayload(entry: ManualFillCareOrderEntry): ManualFillCareOrderPayload {
  const payload: ManualFillCareOrderPayload = {
    ExecType: MANUAL_FILL_EXEC_TYPE,
    // Manual-fill requests must pass the exact remaining quantity; partial quantities cause backend validation errors.
    FillQty: entry.fillQty.value ?? '',
    ...getOptionalPriceAmountPayload(entry),
  };

  if (entry.legEntries != null && entry.legEntries.length > 0) {
    return {
      ...payload,
      LegSummary: entry.legEntries.map(legEntry => ({
        Symbol: legEntry.legSummary.Symbol,
        // Each leg also has to pass its own exact remaining quantity or the backend rejects the manual fill.
        FillQty: legEntry.fillQty.value ?? '',
        ...getOptionalPriceAmountPayload(legEntry),
      })),
    };
  }

  return payload;
}

function getOptionalPriceAmountPayload(entry: Pick<ManualFillCareOrderEntry, 'fillPrice' | 'fillAmount'>) {
  if (entry.fillPrice.isDisabled || entry.fillAmount.isDisabled) {
    return {};
  }

  return {
    FillPrice: entry.fillPrice.value ?? '',
    FillAmount: entry.fillAmount.value ?? '',
  };
}
